/**
 * Runtime Configuration Context
 *
 * Manages loading and accessing configuration from external JSON files.
 * This allows configurations to be modified after build without recompilation.
 *
 * Loading Strategy:
 * 1. At startup, load the default config from /config/default.json
 * 2. When route changes, load route-specific config (e.g., /config/snowflake.json)
 * 3. Fallback to TypeScript configs in development mode if JSON not found
 * 4. Merge route-specific config with default config
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Config } from '../types/config';
import { DemoType } from '../demos';
import { getActiveConfig as getTsConfig, getActiveDemoInfo } from '../demos';

interface RuntimeConfigContextValue {
  config: Config | null;
  loading: boolean;
  error: Error | null;
  refreshConfig: () => Promise<void>;
  loadDemoConfig: (demoId: DemoType) => Promise<void>;
  updateConfig: (updates: Partial<Config>) => void;
}

const RuntimeConfigContext = createContext<RuntimeConfigContextValue | undefined>(undefined);

interface ConfigExport {
  _meta: {
    demoId: string;
    demoName: string;
    description: string;
    route: string;
    exportedAt: string;
    version: string;
  };
  config: Config;
}

/**
 * Fetches a configuration JSON file from the public folder
 */
async function fetchConfigJson(demoId: DemoType): Promise<Config | null> {
  try {
    const response = await fetch(`/config/${demoId}.json`, {
      cache: 'no-store', // Force fresh config on every load
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });

    if (!response.ok) {
      console.warn(`Config JSON not found for ${demoId}, will use TypeScript fallback`);
      return null;
    }

    const data: ConfigExport = await response.json();

    console.log(`✅ Loaded JSON config for ${demoId}`, {
      exportedAt: data._meta?.exportedAt,
      version: data._meta?.version
    });

    return data.config;
  } catch (error) {
    console.warn(`Failed to load JSON config for ${demoId}:`, error);
    return null;
  }
}

/**
 * Gets the fallback TypeScript configuration
 */
function getFallbackConfig(demoId?: DemoType): Config {
  console.log(`⚠️ Using TypeScript fallback config${demoId ? ` for ${demoId}` : ''}`);
  return getTsConfig();
}

interface RuntimeConfigProviderProps {
  children: ReactNode;
}

export const RuntimeConfigProvider: React.FC<RuntimeConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<Config | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Load configuration for a specific demo
   */
  const loadDemoConfig = async (demoId: DemoType) => {
    try {
      setLoading(true);
      setError(null);

      // Try to load JSON config
      const jsonConfig = await fetchConfigJson(demoId);

      if (jsonConfig) {
        setConfig(jsonConfig);
      } else {
        // Fallback to TypeScript config
        const tsConfig = getFallbackConfig(demoId);
        setConfig(tsConfig);
      }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Unknown error loading config');
      setError(errorObj);

      // Even on error, provide fallback config
      const fallbackConfig = getFallbackConfig(demoId);
      setConfig(fallbackConfig);

      console.error('Error loading config:', errorObj);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh the current configuration
   */
  const refreshConfig = async () => {
    const demoInfo = getActiveDemoInfo();
    await loadDemoConfig(demoInfo.id);
  };

  /**
   * Initial load on mount
   */
  useEffect(() => {
    const demoInfo = getActiveDemoInfo();
    loadDemoConfig(demoInfo.id);
  }, []); // Only run once on mount

  const updateConfig = (updates: Partial<Config>) => {
    setConfig(prev => prev ? { ...prev, ...updates } : prev);
  };

  const contextValue: RuntimeConfigContextValue = {
    config,
    loading,
    error,
    refreshConfig,
    loadDemoConfig,
    updateConfig
  };

  // Don't render children until config is loaded
  if (loading || !config) {
    return null;
  }

  return (
    <RuntimeConfigContext.Provider value={contextValue}>
      {children}
    </RuntimeConfigContext.Provider>
  );
};

/**
 * Hook to access the runtime configuration
 * @throws Error if used outside RuntimeConfigProvider
 */
export function useRuntimeConfig(): RuntimeConfigContextValue {
  const context = useContext(RuntimeConfigContext);

  if (context === undefined) {
    throw new Error('useRuntimeConfig must be used within a RuntimeConfigProvider');
  }

  return context;
}

/**
 * Safe version of useRuntimeConfig that returns null instead of throwing
 * Use this when the provider might not be available yet
 */
export function useRuntimeConfigSafe(): RuntimeConfigContextValue | null {
  const context = useContext(RuntimeConfigContext);
  return context ?? null;
}

/**
 * Hook to access a specific configuration value
 */
export function useRuntimeConfigValue<K extends keyof Config>(key: K): Config[K] | undefined {
  const { config } = useRuntimeConfig();
  return config?.[key];
}
